/** Maximum number of records submitted in a single import request. */
export const MAX_IMPORT_RECORDS = 2000;

export type IndividualFieldKey =
  | "FullName"
  | "Gender"
  | "PassportOrIdNumber"
  | "EmailAddress"
  | "PhoneNumber"
  | "OldNid"
  | "UniqueIdentifier"
  | "Recruited";

export type FieldMapping = Partial<Record<IndividualFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const INDIVIDUAL_FIELDS: Array<{
  key: IndividualFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "FullName", label: "Full Name", required: true },
  { key: "Gender", label: "Gender" },
  { key: "PassportOrIdNumber", label: "Passport or ID Number" },
  { key: "EmailAddress", label: "Email Address" },
  { key: "PhoneNumber", label: "Phone Number" },
  { key: "OldNid", label: "Old NID" },
  { key: "UniqueIdentifier", label: "Unique Identifier" },
  { key: "Recruited", label: "Recruited" },
];

function normalize(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(left: string, right: string): number {
  if (left === right) return 2;
  if (left.includes(right) || right.includes(left)) {
    return Math.min(left.length, right.length) / Math.max(left.length, right.length);
  }
  return 0;
}

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of INDIVIDUAL_FIELDS) {
    const normalizedField = normalize(field.key);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const score = similarityScore(normalizedField, normalize(header));
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

function normalizeGender(value: string | null): string | null {
  if (!value) return null;
  const gender = value.toLowerCase();
  if (gender === "male") return "Male";
  if (gender === "female") return "Female";
  if (gender === "unknown" || gender === "unspecified") return "Unspecified";
  return value;
}

function parseBoolean(value: string | null): boolean | null {
  if (!value) return null;
  if (["yes", "true", "1"].includes(value.toLowerCase())) return true;
  if (["no", "false", "0"].includes(value.toLowerCase())) return false;
  return null;
}

/** Converts a CSV row into the shape accepted by the Individuals import API. */
export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping
) {
  const get = (key: IndividualFieldKey): string | null => {
    const column = mappings[key];
    return column ? row[column]?.trim() || null : null;
  };

  return {
    fullName: get("FullName") ?? "",
    gender: normalizeGender(get("Gender")),
    passportOrIdNumber: get("PassportOrIdNumber"),
    emailAddress: get("EmailAddress"),
    phoneNumber: get("PhoneNumber"),
    oldNid: get("OldNid"),
    uniqueIdentifier: get("UniqueIdentifier"),
    recruited: parseBoolean(get("Recruited")),
  };
}
